/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { normalizeBatteryPercentage, roundSpeed } from '../../../../utils';
import { EPlotType, IEcmDiagnosticError, IVehicleDiagnosticsResponse, IVehicleWorkOrderBalloon } from '../../../core/models';
import { BalloonService } from '../../../core/services';
import {
  BalloonClosedByUser,
  CloseSidePanel,
  EBalloonInfoActions,
  ELayoutActions,
  EMapsActions,
  GetVehicleBalloonSuccess,
  OpenEcmPanel,
  OpenInsightsPanel,
  SetSelectedMarkerSuccess
} from '../../../core/store/actions';
import {
  CloseEcmPanel,
  LoadDiagnosticsError,
  LoadDiagnosticsSuccess,
  UpdateDiagnostics
} from '../../../core/store/actions/ecm-panel.actions';
import {
  getEcmPanelDiagnostics,
  getEcmPanelVehicleId,
  getIsEcmEnabledFeatureToggleEnabled,
  getIsEcmPanelOpen
} from '../../../core/store/selectors';
import { IAppState } from '../../../core/store/state';

/**
 * The diagnostics API returns the traction battery charge as a 0..1 fraction (e.g. "0.89"), but the panel's
 * battery widget (fm-ev-battery-section) expects a 0..100 percentage. Normalise it to a percentage string so
 * it matches the socket mapper convention (which already multiplies the charge level by 100). Non-numeric
 * values (e.g. "--") are left untouched.
 */
export function normalizeDiagnosticsBatteryScale(response: IVehicleDiagnosticsResponse): IVehicleDiagnosticsResponse {
  if (!response?.tractionBattery) {
    return response;
  }
  const raw = Number(response.tractionBattery.value);
  // Non-numeric readings (e.g. "--") are left untouched. Numeric values are approximated by the shared
  // helper so the panel and the map balloon scale/round the battery identically.
  if (!Number.isFinite(raw)) {
    return response;
  }
  return {
    ...response,
    tractionBattery: {
      ...response.tractionBattery,
      value: String(normalizeBatteryPercentage(raw))
    }
  };
}

/**
 * The diagnostics API may return the fuel level as a decimal string (e.g. "64.7"), but the panel expects
 * it rounded to the nearest integer to match the socket mapper and balloon display conventions.
 * Non-numeric values (e.g. "--") are left untouched.
 */
export function normalizeDiagnosticsFuelRounding(response: IVehicleDiagnosticsResponse): IVehicleDiagnosticsResponse {
  if (!response?.fuel) {
    return response;
  }
  const fuelValue = Number(response.fuel.value);
  if (!Number.isFinite(fuelValue)) {
    return response;
  }
  return {
    ...response,
    fuel: {
      ...response.fuel,
      value: String(Math.round(fuelValue))
    }
  };
}

/**
 * The diagnostics API returns the speed already converted to the user's units but with decimals
 * (e.g. "31.0686"). The map speed display rounds speed to the nearest whole number
 * (PlotFormattingService.formatSpeed), and the socket mapper does the same, so round the HTTP speed
 * identically via the shared helper. Non-numeric values (e.g. "--") are left untouched.
 */
export function normalizeDiagnosticsSpeedRounding(response: IVehicleDiagnosticsResponse): IVehicleDiagnosticsResponse {
  if (!response?.speed) {
    return response;
  }
  const speedValue = Number(response.speed.value);
  if (!Number.isFinite(speedValue)) {
    return response;
  }
  return {
    ...response,
    speed: {
      ...response.speed,
      value: String(roundSpeed(speedValue))
    }
  };
}

/**
 * TEMPORARY (see `syncEcmPanelFromBalloon$`): overlays the real-time values carried by the map balloon
 * (speed, fuel, traction battery and odometer) onto the current ECM panel diagnostics, preserving every field
 * the balloon does not own: the DTC (kept from the real-time socket), the source, engine hours and the 12V
 * battery. Speed/fuel/battery come from clean balloon fields; the odometer is parsed from the balloon's
 * free-form `customElements` (same string the map balloon shows). No unit conversion is needed here: the
 * balloon already delivers values in the user's units (unlike the socket feed, which sends raw km/h and km) —
 * only rounding/parsing is applied, with the same shared helpers so the panel and the balloon show identical
 * numbers.
 */
export function mergeBalloonIntoDiagnostics(
  current: IVehicleDiagnosticsResponse,
  balloon: IVehicleWorkOrderBalloon
): IVehicleDiagnosticsResponse {
  const merged: IVehicleDiagnosticsResponse = { ...current };

  // Speed: the balloon already carries it in the user's units; round it the way the map/panel do.
  if (balloon.status && Number.isFinite(balloon.status.speed)) {
    merged.speed = {
      value: String(roundSpeed(balloon.status.speed)),
      unitOfMeasure: current.speed?.unitOfMeasure ?? speedUnitFromString(balloon.status.speedString)
    };
  }

  // Fuel: only vehicles expose a fuel percentage (IVehicleEcm); powered assets (IAssetEcm) do not.
  const ecm = balloon.vehicleEcm;
  if (ecm && 'remainingFuelPercentage' in ecm && Number.isFinite(ecm.remainingFuelPercentage)) {
    merged.fuel = {
      value: String(Math.round(ecm.remainingFuelPercentage)),
      unitOfMeasure: current.fuel?.unitOfMeasure ?? '%'
    };
  }

  // Traction battery: `evBatteryData.batteryPercentage` is already 0..100 in the balloon mapping, so
  // normalizeBatteryPercentage is a safe no-op here (values > 1 are returned unchanged).
  if (balloon.evBatteryData && Number.isFinite(balloon.evBatteryData.batteryPercentage)) {
    merged.tractionBattery = {
      chargingState: balloon.evBatteryData.chargingState,
      value: String(normalizeBatteryPercentage(balloon.evBatteryData.batteryPercentage)),
      unitOfMeasure: current.tractionBattery?.unitOfMeasure ?? '%'
    };
  }

  // Odometer: the balloon payload has no dedicated odometer field; the backend only renders it inside the
  // free-form `customElements` (the same string the map balloon shows, e.g. "<b>Odometer </b>37.28"). Parse
  // that value so the panel shows the same reading; the unit is kept from the current diagnostics.
  const odometerValue = parseOdometerFromCustomElements(balloon.customElements);
  if (odometerValue !== null) {
    merged.odometer = {
      value: odometerValue,
      unitOfMeasure: current.odometer?.unitOfMeasure ?? ''
    };
  }

  return merged;
}

/** Extracts the unit label from a balloon speed string, e.g. "14 km/h" -> "km/h", "7 mph" -> "mph". */
function speedUnitFromString(speedString: string | null | undefined): string {
  return (speedString ?? '').replace(/[\d.,]/g, '').trim();
}

/**
 * TEMPORARY: extracts the odometer reading from the balloon's free-form `customElements`. The backend renders
 * the odometer as a standalone bold-labelled entry (e.g. "<b>Odometer </b>37.28"); latitude/longitude live in
 * a different, multi-number entry and are excluded by requiring the whole (tag-stripped) text to be a single
 * "label number" pair. When several candidates exist, the one whose label looks like an odometer wins. Returns
 * the numeric string exactly as rendered (locale decimal preserved, matching the panel's display), or null
 * when no odometer entry is present. Heuristic and locale-dependent — acceptable for this temporary sync.
 */
function parseOdometerFromCustomElements(customElements: string[] | null | undefined): string | null {
  // Fully anchored (^…$) with the two groups separated by a mandatory digit: a run of non-digit characters
  // (the label) followed by a single trailing number. Anchoring both ends means the engine evaluates one
  // position only, and the groups cannot overlap, so there is nothing to backtrack over. A non-digit-only
  // label also naturally excludes multi-number entries such as latitude/longitude. Hoisted so it compiles once.
  const labelThenNumber = /^(\D+)(\d[\d.,]*)$/;
  const candidates: { label: string; value: string }[] = [];
  for (const raw of customElements ?? []) {
    if (!/<b>/i.test(raw)) {
      continue;
    }
    // The balloon only wraps the label in <b>…</b>, so stripping just those tags is enough and keeps the
    // regex trivial (no character-class backtracking).
    const text = raw.replace(/<\/?b>/gi, '').trim();
    const match = labelThenNumber.exec(text);
    if (match === null) {
      continue;
    }
    candidates.push({ label: match[1].trim(), value: match[2] });
  }
  if (candidates.length === 0) {
    return null;
  }
  const odometerLike = candidates.find(candidate => /odom|mile|km|chilometr|kilomet/i.test(candidate.label));
  return (odometerLike ?? candidates[0]).value;
}

@Injectable()
export class EcmPanelEffects {
  // Opening the panel (or switching vehicle) re-triggers the load; switchMap cancels any in-flight request.
  public loadDiagnosticsOnPanelOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType<OpenEcmPanel>(ELayoutActions.OpenEcmPanel),
      switchMap(action =>
        this._balloonService.getVehicleDiagnostics(action.payload.vehicleId).pipe(
          map(response => {
            const normalized = normalizeDiagnosticsBatteryScale(response);
            const withRoundedFuel = normalizeDiagnosticsFuelRounding(normalized);
            const withRoundedSpeed = normalizeDiagnosticsSpeedRounding(withRoundedFuel);
            return new LoadDiagnosticsSuccess(withRoundedSpeed);
          }),
          catchError(error => of(new LoadDiagnosticsError({ errors: this._toDiagnosticErrors(error) })))
        )
      )
    )
  );

  /**
   * TEMPORARY — remove once the real-time ECM socket feed is trustworthy again.
   *
   * The socket feed is currently unreliable for most fields, so it is configured to update only the DTC count
   * (see EcmDiagnosticsSocketEffects / the realTimeSocketValuesToIgnore config). To still keep the ECM side
   * panel in sync with the map balloon while a vehicle moves, this effect consumes the balloon data the map
   * balloon has ALREADY fetched (`GetVehicleBalloonSuccess`) and overlays its speed, fuel, traction-battery and
   * odometer values onto the panel diagnostics via `UpdateDiagnostics`. It does not modify the balloon effects: it only
   * listens to their success action and dispatches into the ECM store.
   *
   * It runs only when the ECM feature is enabled, the panel is open on the SAME vehicle as the balloon, and a
   * baseline diagnostics object already exists. It syncs speed, fuel, traction battery and odometer, and never
   * touches the DTC (which stays owned by the real-time socket).
   *
   * WHEN THE SOCKET IS FIXED: delete this effect and let EcmDiagnosticsSocketEffects.updateVehicleDiagnostic$
   * drive all real-time fields again.
   */
  public syncEcmPanelFromBalloon$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetVehicleBalloonSuccess>(EBalloonInfoActions.GetVehicleBalloonSuccess),
      map(action => action.payload),
      withLatestFrom(
        this._store.pipe(select(getIsEcmEnabledFeatureToggleEnabled)),
        this._store.pipe(select(getIsEcmPanelOpen)),
        this._store.pipe(select(getEcmPanelVehicleId)),
        this._store.pipe(select(getEcmPanelDiagnostics))
      ),
      filter(
        ([balloon, isEcmFeatureEnabled, isPanelOpen, panelVehicleId, diagnostics]) =>
          isEcmFeatureEnabled && isPanelOpen && diagnostics !== null && balloon.id === panelVehicleId
      ),
      map(([balloon, , , , diagnostics]) => new UpdateDiagnostics(mergeBalloonIntoDiagnostics(diagnostics!, balloon)))
    )
  );

  /**
   * Keeps the ECM panel in sync with the currently selected object while it is open. Both a map balloon/marker
   * click and a left-list selection funnel through `SetSelectedMarkerSuccess`. It only runs when the ECM feature
   * toggle is enabled and the panel is open. While the panel is open:
   *  - selecting a DIFFERENT vehicle or powered asset reloads the panel for that vehicle;
   *  - selecting an object that has no diagnostics panel (non-powered asset, place, geofence, ...) closes the panel;
   *  - selecting the same vehicle again is a no-op.
   */
  public syncEcmPanelWithSelectedMarker$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SetSelectedMarkerSuccess>(EMapsActions.SetSelectedMarkerSuccess),
      map(action => action.payload),
      withLatestFrom(
        this._store.pipe(select(getIsEcmEnabledFeatureToggleEnabled)),
        this._store.pipe(select(getIsEcmPanelOpen)),
        this._store.pipe(select(getEcmPanelVehicleId))
      ),
      // Only sync when the ECM feature is enabled for the user and the panel is currently open.
      filter(([, isEcmFeatureEnabled, isPanelOpen]) => isEcmFeatureEnabled && isPanelOpen),
      mergeMap(([marker, , , currentVehicleId]) => {
        // The diagnostics panel can only be opened for vehicles and powered assets.
        const canAccessDiagnosticsPanel = marker.type === EPlotType.vehicle || marker.type === EPlotType.poweredAsset;

        // Selecting an object without a diagnostics panel closes it (reset the slice, hide the side panel, restore insights).
        if (!canAccessDiagnosticsPanel) {
          return [new CloseEcmPanel(), new CloseSidePanel(), new OpenInsightsPanel()];
        }

        // A different vehicle/powered asset reloads the diagnostics; the same one leaves the panel untouched.
        if (marker.id !== currentVehicleId) {
          return [new OpenEcmPanel({ vehicleName: marker.title, vehicleId: marker.id, plotType: marker.type })];
        }

        return [];
      })
    )
  );

  /**
   * When the user explicitly closes the map balloon (its X), also dismiss the ECM diagnostics side panel.
   * This reacts to `BalloonClosedByUser` rather than the generic `CloseBalloon`, so programmatic closes
   * (e.g. marker re-plotting while switching vehicles) do NOT tear the panel down.
   */
  public closeEcmPanelOnUserBalloonClose$ = createEffect(() =>
    this._actions$.pipe(
      ofType<BalloonClosedByUser>(EBalloonInfoActions.BalloonClosedByUser),
      withLatestFrom(this._store.pipe(select(getIsEcmPanelOpen))),
      filter(([, isEcmPanelOpen]) => isEcmPanelOpen),
      mergeMap(() => [new CloseEcmPanel(), new CloseSidePanel(), new OpenInsightsPanel()])
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _balloonService: BalloonService,
    private readonly _store: Store<IAppState>
  ) {}

  /**
   * Normalises a failed diagnostics response into the structured error array the panel state expects.
   * When the backend supplies its own array of `{ code, error, description }` it is passed through;
   * otherwise the HTTP error is mapped to a single entry.
   */
  private _toDiagnosticErrors(error: any): IEcmDiagnosticError[] {
    if (Array.isArray(error?.error)) {
      return error.error as IEcmDiagnosticError[];
    }

    return [
      {
        code: error?.status == null ? 'UNKNOWN' : String(error.status),
        error: error?.statusText || error?.name || 'Error',
        description: error?.message || 'Failed to load diagnostics.'
      }
    ];
  }
}
